<!--
  Mitglieder der eigenen Organisation selbst verwalten (07.09.2026) — bisher
  ging das nur über die KF-Verwaltung (Personal). Nur für den Inhaber sichtbar
  (App.svelte blendet den Reiter sonst aus, die Route selbst ist serverseitig
  ebenso gesperrt).
-->
<script lang="ts">
  import { api } from '../lib/api.ts';
  import { sitzung } from '../lib/sitzung.svelte.ts';

  type Rolle = 'inhaber' | 'mitglied';

  interface Mitglied {
    id: string;
    email: string;
    name: string | null;
    rolle: Rolle;
    funktion: string | null;
    gesperrt_am: string | null;
    gesperrt_grund: string | null;
    zaehlt_als_sitz: boolean;
    passwort_gesetzt: boolean;
    einladung_offen: boolean;
    /**
     * Nur bei Familie aussagekräftig (Vereinfachungsplan Teil 4) — siehe
     * ausführliche Begründung in web/src/lib/Zugaenge.svelte.
     */
    vault_aktiv: boolean | null;
    das_aktiv: boolean | null;
  }

  const ROLLENNAME: Record<Rolle, string> = {
    inhaber: 'Inhaber',
    mitglied: 'Mitglied',
  };

  let konten = $state<Mitglied[]>([]);
  let sitze = $state<{ belegt: number; grenze: number; typ: string } | null>(null);
  let laedt = $state(true);
  let ladefehler = $state<string | null>(null);
  let fehler = $state<string | null>(null);

  async function laden(): Promise<void> {
    laedt = true;
    try {
      const antwort = await api.get<{ konten: Mitglied[]; sitze: { belegt: number; grenze: number; typ: string } }>(
        '/api/portal/organisation/mitglieder',
      );
      konten = antwort.konten;
      sitze = antwort.sitze;
      ladefehler = null;
    } catch (f) {
      ladefehler = f instanceof Error ? f.message : 'Mitglieder nicht ladbar';
    } finally {
      laedt = false;
    }
  }

  $effect(() => {
    void laden();
  });

  // --- Einladen ---------------------------------------------------------

  let neu = $state({ email: '', name: '', rolle: 'mitglied' as Rolle, funktion: '' });
  let einladenLaeuft = $state(false);
  let linkHinweis = $state<string | null>(null);
  let link = $state<string | null>(null);

  async function einladen(ereignis: SubmitEvent): Promise<void> {
    ereignis.preventDefault();
    einladenLaeuft = true;
    fehler = null;
    linkHinweis = null;
    link = null;
    try {
      const antwort = await api.post<{ mail_versendet: boolean; link: string | null }>(
        '/api/portal/organisation/mitglieder',
        {
          email: neu.email,
          name: neu.name.trim() || undefined,
          rolle: neu.rolle,
          funktion: neu.funktion.trim() || undefined,
        },
      );
      if (!antwort.mail_versendet) {
        linkHinweis = 'Einladung erzeugt. Ohne Mailversand bitte diesen Link weitergeben:';
        link = antwort.link;
      }
      neu = { email: '', name: '', rolle: 'mitglied', funktion: '' };
      await laden();
    } catch (f) {
      fehler = f instanceof Error ? f.message : 'Einladen fehlgeschlagen';
    } finally {
      einladenLaeuft = false;
    }
  }

  // --- Rolle ändern (auch: Inhaberschaft übertragen) ---------------------

  async function rolleAendern(konto: Mitglied, rolle: Rolle): Promise<void> {
    if (konto.rolle === rolle) return;
    try {
      await api.patch(`/api/portal/organisation/mitglieder/${konto.id}`, { rolle });
      fehler = null;
      await laden();
    } catch (f) {
      fehler = f instanceof Error ? f.message : 'Rolle ändern fehlgeschlagen';
      await laden();
    }
  }

  // --- Sperren/Freigeben (Migration 054) ---------------------------------

  let sperrenZiel = $state<Mitglied | null>(null);
  let sperrenGrund = $state('');
  let sperrenLaeuft = $state(false);

  async function sperrenBestaetigen(): Promise<void> {
    if (!sperrenZiel || !sperrenGrund.trim()) return;
    sperrenLaeuft = true;
    try {
      await api.patch(`/api/portal/organisation/mitglieder/${sperrenZiel.id}`, {
        gesperrt: true,
        grund: sperrenGrund.trim(),
      });
      sperrenZiel = null;
      sperrenGrund = '';
      fehler = null;
      await laden();
    } catch (f) {
      fehler = f instanceof Error ? f.message : 'Sperren fehlgeschlagen';
    } finally {
      sperrenLaeuft = false;
    }
  }

  async function freigeben(konto: Mitglied): Promise<void> {
    try {
      await api.patch(`/api/portal/organisation/mitglieder/${konto.id}`, { gesperrt: false });
      fehler = null;
      await laden();
    } catch (f) {
      fehler = f instanceof Error ? f.message : 'Freigeben fehlgeschlagen';
    }
  }

  // --- Entfernen ----------------------------------------------------------

  let entfernenZiel = $state<Mitglied | null>(null);
  let entfernenLaeuft = $state(false);

  async function entfernenBestaetigen(): Promise<void> {
    if (!entfernenZiel) return;
    entfernenLaeuft = true;
    try {
      await api.del(`/api/portal/organisation/mitglieder/${entfernenZiel.id}`);
      entfernenZiel = null;
      fehler = null;
      await laden();
    } catch (f) {
      fehler = f instanceof Error ? f.message : 'Entfernen fehlgeschlagen';
    } finally {
      entfernenLaeuft = false;
    }
  }
</script>

<h1>Organisation</h1>
<p class="leise">{sitzung.kunde?.anzeigename}</p>

{#if fehler}<p class="fehler">{fehler}</p>{/if}

<div class="karte">
  <h2>Mitglieder</h2>
  {#if ladefehler}<p class="fehler">{ladefehler}</p>{/if}
  {#if sitze}
    <p class="klein leise">
      {sitze.belegt} von {sitze.grenze} {sitze.grenze === 1 ? 'Platz' : 'Plätzen'} belegt.
    </p>
  {/if}

  {#if laedt}
    <p class="leise">lädt …</p>
  {:else if konten.length === 0}
    <p class="leise">Noch keine Mitglieder.</p>
  {:else}
    <ul class="mitgliederliste">
      {#each konten as konto (konto.id)}
        <li>
          {#if entfernenZiel?.id === konto.id}
            <div class="bestaetigung">
              <p class="wichtig">
                {konto.email} gehört danach nicht mehr zu {sitzung.kunde?.anzeigename} und sieht die Daten
                nicht mehr. Der Sitz wird frei.
              </p>
              <div class="knopfreihe">
                <button type="button" class="gefahr" disabled={entfernenLaeuft} onclick={entfernenBestaetigen}>
                  Entfernen
                </button>
                <button type="button" class="sekundaer" onclick={() => (entfernenZiel = null)}>
                  Abbrechen
                </button>
              </div>
            </div>
          {:else if sperrenZiel?.id === konto.id}
            <div class="bestaetigung">
              <label for="sperren-grund">Warum wird {konto.email} für diese Organisation gesperrt?</label>
              <input id="sperren-grund" type="text" bind:value={sperrenGrund} placeholder="Grund" />
              <p class="klein leise">Der Sitz bleibt belegt — niemand sonst kann ihn währenddessen bekommen.</p>
              <div class="knopfreihe">
                <button
                  type="button"
                  class="gefahr"
                  disabled={!sperrenGrund.trim() || sperrenLaeuft}
                  onclick={sperrenBestaetigen}
                >
                  Sperren
                </button>
                <button
                  type="button"
                  class="sekundaer"
                  onclick={() => {
                    sperrenZiel = null;
                    sperrenGrund = '';
                  }}
                >
                  Abbrechen
                </button>
              </div>
            </div>
          {:else}
            <div class="stand">
              <strong>{konto.email}</strong>
              {#if konto.name}<span class="leise"> · {konto.name}</span>{/if}
              <span class="rollenschild">{ROLLENNAME[konto.rolle]}</span>
              {#if konto.funktion}<span class="leise"> · {konto.funktion}</span>{/if}
              {#if sitze?.typ === 'familie'}
                <span class="leise klein">
                  · Passwort-Safe {konto.vault_aktiv ? '✓' : '—'} · Akten-Safe {konto.das_aktiv ? '✓' : '—'}
                </span>
              {/if}
              <div class="leise klein">
                {#if konto.gesperrt_am}
                  gesperrt{konto.gesperrt_grund ? ` (${konto.gesperrt_grund})` : ''}
                {:else if konto.einladung_offen}
                  eingeladen, Passwort noch nicht gesetzt
                {:else if konto.passwort_gesetzt}
                  aktiv
                {:else}
                  Einladung abgelaufen
                {/if}
              </div>
            </div>
            <div class="aktionen">
              <select
                aria-label={`Rolle von ${konto.email}`}
                value={konto.rolle}
                onchange={(e) => rolleAendern(konto, e.currentTarget.value as Rolle)}
              >
                <option value="inhaber">Inhaber</option>
                <option value="mitglied">Mitglied</option>
              </select>
              {#if konto.gesperrt_am}
                <button type="button" class="sekundaer" onclick={() => freigeben(konto)}>Freigeben</button>
              {:else}
                <button type="button" class="sekundaer" onclick={() => (sperrenZiel = konto)}>Sperren</button>
              {/if}
              <button type="button" class="sekundaer" onclick={() => (entfernenZiel = konto)}>
                Entfernen
              </button>
            </div>
          {/if}
        </li>
      {/each}
    </ul>
  {/if}
</div>

<div class="karte">
  <h2>Mitglied einladen</h2>
  <p class="klein leise">
    Ein neues Mitglied bekommt einen einmaligen Link und vergibt sein Passwort selbst — wir
    verschicken grundsätzlich keine Passwörter. Eine Adresse, die es bei uns schon gibt, wird
    nicht doppelt angelegt: Die Person bekommt diese Organisation zusätzlich.
  </p>
  {#if linkHinweis}<p class="wichtig">{linkHinweis}</p>{/if}
  {#if link}<p class="link"><code>{link}</code></p>{/if}

  <form onsubmit={einladen}>
    <label for="e-email">E-Mail</label>
    <input id="e-email" type="email" bind:value={neu.email} required />

    <label for="e-name">Name (optional)</label>
    <input id="e-name" type="text" bind:value={neu.name} />

    <label for="e-funktion">Funktion (optional)</label>
    <input id="e-funktion" type="text" bind:value={neu.funktion} placeholder="z. B. 1. Vorsitzender" />

    <label for="e-rolle">Rolle</label>
    <select id="e-rolle" bind:value={neu.rolle}>
      <option value="mitglied">Mitglied</option>
      <option value="inhaber">Inhaber</option>
    </select>

    <div class="knopfreihe">
      <button type="submit" disabled={einladenLaeuft || !neu.email.includes('@')}>
        {einladenLaeuft ? 'Lädt ein …' : 'Einladen'}
      </button>
    </div>
  </form>
</div>

<style>
  .link code {
    display: block;
    padding: 0.5rem 0.75rem;
    background: var(--hintergrund);
    border: 1px solid var(--rand);
    border-radius: var(--radius);
    word-break: break-all;
    font-size: 0.85rem;
  }

  .mitgliederliste {
    list-style: none;
    padding: 0;
    margin: 0.5rem 0 0;
  }

  .mitgliederliste li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
    padding: 0.6rem 0;
    border-bottom: 1px solid var(--rand);
  }

  .stand {
    min-width: 14rem;
    flex: 1;
  }

  .aktionen {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    align-items: center;
  }

  .aktionen select {
    width: auto;
    margin: 0;
  }

  .rollenschild {
    font-size: 0.75rem;
    padding: 0.1rem 0.45rem;
    border: 1px solid var(--rand);
    border-radius: var(--radius-pill);
    white-space: nowrap;
  }

  .bestaetigung {
    width: 100%;
  }

  .klein {
    font-size: 0.85rem;
  }
</style>
